'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BUSINESS_INFORMATION_ROUTE,
  CUSTOMER_INFORMATION_ROUTE,
  INITIAL_INFORMATION_ROUTE,
  LANDING_PAGE_ROUTE,
  RECEIPT_ROUTE,
  SUMMARY_PAYMENT_ROUTE,
} from '../../../../../i18n/pathnames/licenses';
import { trackEvent } from '../../../../../src/components/tracking/eventSender';
import { useCurrentStepRoute } from '../../components/useCurrentStepRoute';
import { FlowController } from '../FlowController';
import { LicenseFlowTypeValue, Step } from '../flows';
import { useProgressValidator } from '../hooks/useProgressValidator';
import { useLicenseStore } from '../licenseStore';
import { SanityLicense } from '../types/licenses';

type Event = {
  event: string;
  event_category: string;
  event_action: string;
  event_name: string;
  event_value: number;
};

export const LicenseEventTracker = ({ license }: { license: SanityLicense }) => {
  const [previousStepRoute, setPreviousStepRoute] = useState('');
  const [previousLicense, setPreviousLicense] = useState('');
  const currentStepRoute = useCurrentStepRoute();
  const progress = useProgressValidator(license);

  const flow = useMemo(() => {
    return new FlowController(license);
  }, [license]);

  const eventCategory = (flowType?: LicenseFlowTypeValue): string | undefined => {
    switch (flowType) {
      case 'A':
        return 'licens_flow_A';
      case 'B':
        return 'licens_flow_B';
      case 'C':
        return 'licens_flow_C';
      case 'D':
        return 'licens_flow_D';
      default:
        return undefined;
    }
  };

  const eventValue = (): number | undefined => {
    const licensePlan = useLicenseStore.getState().licensePlan;
    if (!licensePlan) return undefined;

    if (licensePlan.priceExclTax != null) {
      return licensePlan.priceExclTax;
    }

    if (licensePlan.recurringPeriodPrice != null) {
      return licensePlan.recurringPeriodPrice;
    }

    return undefined;
  };

  const eventAction = useCallback(
    (step?: Step): string | undefined => {
      const countedSteps = flow.getCountedSteps();
      const countedIndex = currentStepRoute ? countedSteps.findIndex(s => s.stepRoute === currentStepRoute) : -1;
      const stepIndex =
        1 +
        (currentStepRoute === RECEIPT_ROUTE
          ? countedSteps.length
          : countedIndex >= 0
            ? countedIndex
            : progress.currentStepIndex);
      switch (step?.stepRoute) {
        case INITIAL_INFORMATION_ROUTE:
          return `${stepIndex}_forestillingsinfo`;
        case BUSINESS_INFORMATION_ROUTE:
          return `${stepIndex}_musikbrugsinfo`;
        case LANDING_PAGE_ROUTE:
          return `${stepIndex}_licensinfo`;
        case CUSTOMER_INFORMATION_ROUTE:
          return `${stepIndex}_kontaktinfo`;
        case SUMMARY_PAYMENT_ROUTE:
          return `${stepIndex}_opsummering/betaling`;
        case RECEIPT_ROUTE:
          return `${stepIndex}_kvittering`;
        default:
          return undefined;
      }
    },
    [currentStepRoute, flow, progress.currentStepIndex],
  );

  const formatName = (name: string) => {
    return name.replace(/\s+/g, '_').toLocaleLowerCase();
  };

  useEffect(() => {
    const event_category = eventCategory(flow.flowType);
    const event_action = eventAction(currentStepRoute ? flow.getCurrentStep(currentStepRoute) : undefined);
    const event_name = license.licenseId ? formatName(license.licenseId) : undefined;

    if (!event_category || !event_action || !event_name) {
      return;
    }
    const value = eventValue() ?? 0;
    const event: Event = {
      event: 'track_event',
      event_category,
      event_action,
      event_name,
      event_value: value,
    };

    const newRoute = currentStepRoute !== previousStepRoute;
    if (newRoute) setPreviousStepRoute(currentStepRoute ?? '');

    const newLicense = license.licenseId !== previousLicense;
    if (newLicense) setPreviousLicense(license.licenseId ?? '');

    if (newRoute || newLicense) {
      trackEvent(event);
    }
  }, [currentStepRoute, eventAction, flow, license, previousLicense, previousStepRoute]);

  return null;
};
